import React, { useState, useCallback, useEffect } from 'react';
import { Container, Row, Col, Alert, Button } from 'reactstrap';
import AccessoPrenotazione from './AccessoPrenotazione';
import isilogo from './images/ISI_LOGO_2.jpg';
import Slot from './Slot';
import CancellaPrenotazione from './CancellaPrenotazione'; 

function Prenotazione() {
  const [codiceValidato, setCodiceValidato] = useState(sessionStorage.getItem('codiceValidato') || false);
  const [voucherID, setVoucherID] = useState(sessionStorage.getItem('voucherID') || null);
  const [associatedID, setAssociatedID] = useState(sessionStorage.getItem('associatedID') || null);
  const [slots, setSlots] = useState({});
  const [selectedSlot, setSelectedSlot] = useState({ giorno: null, ora: null, id_ambulatorio: null });
  const [currentSlot, setCurrentSlot] = useState({ 
    giorno: sessionStorage.getItem('currentSlotDay') || null, 
    ora: sessionStorage.getItem('currentSlotHour') || null,
    id_ambulatorio: sessionStorage.getItem('currentSlotAmbulatorio') || null
  });
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVisible, setAlertVisible] = useState(false);
  const [associatedName, setAssociatedName] = useState(
    sessionStorage.getItem('nomeAssociato') && sessionStorage.getItem('cognomeAssociato')
      ? `${capitalizeWords(sessionStorage.getItem('nomeAssociato'))} ${capitalizeWords(sessionStorage.getItem('cognomeAssociato'))}`
      : null
  );
  const [alertColor, setAlertColor] = useState('danger');

  const SESSION_DURATION = 10 * 60 * 1000; // 10 minuti in ms
  const today = new Date();

  function capitalizeWords(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // ⬇️ Raggruppa slot per giorno, mantenendo anche id_ambulatorio
  const groupSlotsByDay = useCallback((slots) => {
    return slots.reduce((acc, slot) => {
      if (!acc[slot.giorno]) {
        acc[slot.giorno] = [];
      }
      acc[slot.giorno].push({
        ora: slot.ora,
        id_ambulatorio: slot.id_ambulatorio
      });
      return acc;
    }, {});
  }, []);

  const showAlert = useCallback((message, color = 'danger') => {
    if (message) {
      setAlertMessage(message);
      setAlertColor(color);
      setAlertVisible(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setAlertVisible(false);
    }
  }, []);

  const handleValidazione = useCallback((isValid, id_voucher, id_associato, data_visita, ora_visita, nome_associato, cognome_associato, codice_prenotazione, id_ambulatorio) => {
    if (isValid) {
      setCodiceValidato(true);
      setVoucherID(id_voucher);
      setAssociatedID(id_associato);
      setCurrentSlot({ giorno: data_visita, ora: ora_visita, id_ambulatorio });
      setAssociatedName(`${capitalizeWords(nome_associato)} ${capitalizeWords(cognome_associato)}`);      
      sessionStorage.setItem('codiceValidato', true);
      sessionStorage.setItem('voucherID', id_voucher);
      sessionStorage.setItem('associatedID', id_associato);
      sessionStorage.setItem('currentSlotDay', data_visita);
      sessionStorage.setItem('currentSlotHour', ora_visita);
      sessionStorage.setItem('currentSlotAmbulatorio', id_ambulatorio || '');
      sessionStorage.setItem('nomeAssociato', nome_associato);
      sessionStorage.setItem('cognomeAssociato', cognome_associato);
      sessionStorage.setItem('codiceValidatoTimestamp', Date.now().toString());
      sessionStorage.setItem('codicePrenotazione', codice_prenotazione);
    } else {
      setCodiceValidato(false);
      setVoucherID(null);
      setAssociatedID(null);
      setCurrentSlot(null);
      sessionStorage.clear();
    }
  }, []);

  const handleSlotSelection = useCallback((giorno, ora, id_ambulatorio) => {
    setSelectedSlot({ giorno, ora, id_ambulatorio });
  }, []);

  // ⬇️ Aggiunto id_ambulatorio alla fetch
  const fetchSlots = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_SERVER_URI}/visits/availableslots`);
      if (!response.ok) {
        throw new Error('Errore durante il recupero degli slot');
      }
      const data = await response.json();
      // Assumiamo che ogni oggetto di data abbia { giorno, ora, id_ambulatorio }
      setSlots(groupSlotsByDay(data));
    } catch (error) {
      console.error('Errore:', error);
      showAlert('Errore nel caricamento degli slot disponibili');
    }
  }, [groupSlotsByDay, showAlert]);

  useEffect(() => {
    const timestamp = sessionStorage.getItem('codiceValidatoTimestamp');
    if (timestamp && Date.now() - parseInt(timestamp, 10) > SESSION_DURATION) {
      sessionStorage.clear();
      handleValidazione(false, null, null, null, null, null, null);
      showAlert('Sessione scaduta. Inserisci nuovamente il codice prenotazione.');
    }
    // eslint-disable-next-line
  }, [codiceValidato, fetchSlots]);

  return (
    <Container>
      <Row>
        <Col xs="auto">
          <img className="isilogo" src={isilogo} width="120" alt="Logo Il sorriso di Isa" />
        </Col>
        <Col className="d-flex align-items-center">
          <h2>Prenotazione visite</h2>
        </Col>
      </Row>

      {alertVisible && (
        <Row>
          <Col>
            <Alert fade={false} color={alertColor} toggle={() => setAlertVisible(false)}>
              {alertMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {!codiceValidato ? (
        <Row>
          <AccessoPrenotazione onValidazione={handleValidazione} showAlert={showAlert}/>
        </Row>
      ) : currentSlot.giorno && new Date(currentSlot.giorno) > today ? (
        <CancellaPrenotazione 
          currentSlot={currentSlot} 
          setSelectedSlot={setSelectedSlot}
          setSlots={setSlots} 
          fetchSlots={fetchSlots}
          associatedID={associatedID}
          associatedName={associatedName}
          voucherID={voucherID}
          selectedSlot={selectedSlot}
          showAlert={showAlert}
          setCurrentSlot={setCurrentSlot}
          handleValidazione={handleValidazione}
        />
      ) : currentSlot.giorno && new Date(currentSlot.giorno) < today ? (
        <Container>
          <Alert color="warning">
            Questo voucher per {associatedName} è già stato utilizzato il {new Date(currentSlot.giorno).toLocaleDateString('it-IT')}
            {' '} alle ore {currentSlot.ora.slice(11, 16)}.
          </Alert>
          <Row className="justify-content-center">
            <Col xs="auto">
              <Button
                color="secondary"
                className="mb-3"
                onClick={() => {
                  showAlert(null);
                  handleValidazione(false, null, null, null, null, null, null);
                }}
              >
                Cambia codice prenotazione
              </Button>       
            </Col> 
          </Row>
        </Container>
      ) : (
        <Slot
          slots={slots}
          selectedSlot={selectedSlot}
          handleSlotSelection={handleSlotSelection}
          associatedID={associatedID} 
          associatedName={associatedName}
          voucherID={voucherID}
          showAlert={showAlert} 
          fetchSlots={fetchSlots}
          setSelectedSlot={setSelectedSlot}
          setSlots={setSlots}
          setCurrentSlot={setCurrentSlot}
          handleValidazione={handleValidazione}
        />  
      )}
    </Container>
  );
}

export default Prenotazione;